// pages/DashboardPage.tsx
import { useState, useEffect } from 'react';
import { auth, db } from '../firebaseConfig';
import { collection, addDoc, onSnapshot, query, orderBy, doc, deleteDoc, where, getDocs, updateDoc } from 'firebase/firestore';
import { motion } from 'framer-motion';
import { FaCalendarPlus, FaTrash, FaSignOutAlt, FaEye, FaEyeSlash } from 'react-icons/fa';
import { UsageChart } from '../components/DashboardUsageChart';
import { LocationsManager } from '../components/MapsLocationsManager';
import { SpotifySlotAdmin } from '../components/SpotifySlotAdmin';
import { Message, SpecialDate, Recommendation } from '../types/types';

export const DashboardPage = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [specialDates, setSpecialDates] = useState<SpecialDate[]>([]);
  const [newDate, setNewDate] = useState('');
  const [newTitle, setNewTitle] = useState('');

  // Funciones para manejar mensajes
  const handleToggleMessageVisibility = async (messageId: string, currentVisibility: boolean) => {
    try {
      const messageRef = doc(db, 'messages', messageId);
      await updateDoc(messageRef, {
        isVisible: !currentVisibility
      });
    } catch (error) {
      console.error('Error toggling message visibility:', error);
    }
  };

  const handleDeleteMessage = async (messageId: string) => {
    const isConfirmed = window.confirm('¿Seguro que quieres eliminar este mensaje?');
    if (isConfirmed) {
      try {
        await deleteDoc(doc(db, 'messages', messageId));
      } catch (error) {
        console.error('Error deleting message:', error);
      }
    }
  };

  const handleChangeMessageColor = async (messageId: string, newColor: string) => {
    try {
      const messageRef = doc(db, 'messages', messageId);
      await updateDoc(messageRef, {
        color: newColor
      });
    } catch (error) {
      console.error('Error changing message color:', error);
    }
  };

  // Efectos para cargar datos
  useEffect(() => {
    const q = query(collection(db, 'messages'), orderBy('timestamp', 'desc'));
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const msgs: Message[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        msgs.push({
          id: doc.id,
          text: data.text,
          timestamp: data.timestamp ? data.timestamp.toDate() : new Date(),
          from: data.from || 'unknown',
          color: data.color,
          isVisible: data.isVisible !== false
        });
      });
      setMessages(msgs);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const q = query(collection(db, 'specialDates'), orderBy('date', 'asc'));
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const dates: SpecialDate[] = [];
      querySnapshot.forEach((doc) => {
        dates.push({ id: doc.id, ...doc.data() } as SpecialDate);
      });
      setSpecialDates(dates);
    });
    return () => unsubscribe();
  }, []);


  // Funciones de manejo
  const handleAddSpecialDate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newDate && newTitle) {
      await addDoc(collection(db, 'specialDates'), {
        date: newDate,
        title: newTitle,
      });
      setNewDate('');
      setNewTitle('');
    }
  };

  const handleDeleteDate = async (id: string) => {
    const isConfirmed = window.confirm('¿Seguro que quieres borrar esta fecha?');
    if (isConfirmed) {
      await deleteDoc(doc(db, 'specialDates', id));
    }
  };

  const handleLogout = () => {
    auth.signOut();
  };

  return (
    <main className="bg-background min-h-screen w-full p-4 sm:p-6 lg:p-8">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
        className="max-w-7xl mx-auto"
      >
        <header className="flex justify-between items-center mb-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-text-main">Dashboard de Mau</h2>
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={handleLogout}
            title="Cerrar Sesión"
            className="flex items-center justify-center w-10 h-10 bg-white/60 hover:bg-white rounded-full shadow-md text-text-light hover:text-primary transition-all duration-300"
          >
            <FaSignOutAlt />
          </motion.button>
        </header>

        {/* Gráfica de uso */}
        <section className="mb-12">
          <h3 className="text-2xl font-bold text-text-main mb-4">Actividad de la Página</h3>
          <UsageChart />
        </section>
        
        {/* Grid principal con 2 columnas */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">

          {/* Columna 1: Títulos Especiales */}
          <section>
            <h3 className="text-2xl font-bold text-text-main mb-4">Títulos Especiales</h3>
            <form onSubmit={handleAddSpecialDate} className="bg-white/50 p-4 rounded-xl shadow-lg space-y-3 mb-6">
              <input 
                type="date" 
                value={newDate} 
                onChange={(e) => setNewDate(e.target.value)} 
                required 
                className="w-full p-2 rounded-lg border border-gray-300" 
              />
              <input 
                type="text" 
                placeholder="Título del evento (ej. ¡Feliz Aniversario!)" 
                value={newTitle} 
                onChange={(e) => setNewTitle(e.target.value)} 
                required 
                className="w-full p-2 rounded-lg border border-gray-300" 
              />
              <button 
                type="submit" 
                className="w-full py-2 bg-primary text-white font-bold rounded-lg hover:bg-primary-hover transition-colors flex items-center justify-center gap-2"
              >
                <FaCalendarPlus /> Añadir Fecha
              </button>
            </form>
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {specialDates.map(d => (
                <div key={d.id} className="bg-white/40 p-3 rounded-lg flex justify-between items-center">
                  <div>
                    <p className="font-semibold">{d.title}</p>
                    <p className="text-sm text-text-light">{d.date}</p>
                  </div>
                  <button onClick={() => handleDeleteDate(d.id)} className="text-red-500 hover:text-red-700 p-2">
                    <FaTrash />
                  </button>
                </div>
              ))}
            </div>
          </section>

          {/* Columna 2: Nubes de Mensaje */}
          <section>
            <h3 className="text-2xl font-bold text-text-main mb-4">Nubes de Mensaje</h3>
            <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2">
              {messages.map(msg => (
                <div key={msg.id} className="bg-white/40 p-4 rounded-xl shadow-md">
                  <p className="text-text-main">{msg.text}</p>
                  <p className="text-xs text-right text-text-light mt-2">{msg.timestamp?.toLocaleString()}</p>

                  {/* Color Picker */}
                  <div className="mt-3 flex items-center gap-2">
                    <label className="text-sm text-text-main font-semibold">Color:</label>
                    <input
                      type="color"
                      value={msg.color || '#3b82f6'}
                      onChange={(e) => handleChangeMessageColor(msg.id, e.target.value)}
                      className="w-12 h-8 rounded cursor-pointer border-2 border-gray-300"
                      title="Cambiar color de la nube"
                    />
                    <span className="text-xs text-text-light">{msg.color || 'Azul por defecto'}</span>
                  </div>

                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={() => handleToggleMessageVisibility(msg.id, msg.isVisible || false)}
                      className="flex-1 py-2 px-4 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600 transition-colors flex items-center justify-center gap-2"
                      title={msg.isVisible ? 'Ocultar del hero' : 'Mostrar en hero'}
                    >
                      {msg.isVisible ? <FaEyeSlash /> : <FaEye />}
                      {msg.isVisible ? 'Ocultar' : 'Mostrar'}
                    </button>
                    <button
                      onClick={() => handleDeleteMessage(msg.id)}
                      className="flex-1 py-2 px-4 bg-red-500 text-white text-sm rounded-lg hover:bg-red-600 transition-colors flex items-center justify-center gap-2"
                    >
                      <FaTrash />
                      Eliminar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* NUEVA SECCIÓN: Administrador de Ubicaciones */}
        <section className="mb-12">
          <h3 className="text-2xl font-bold text-text-main mb-4">🗺️ Gestión de Lugares</h3>
          <LocationsManager />
        </section>

        {/* NUEVA SECCIÓN: Administración de Spotify Slots */}
        <section className="mb-12">
          <SpotifySlotAdmin />
        </section>
      </motion.div>
    </main>
  );
};